// src/pages/SchedulerStudent.tsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";

/* === Firestore (Scheduler is EU-ready) === */
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { fs } from "../firebase";

/**
 * Firestore data model (EU-ready; deploy Firestore in eur3):
 *
 *  /scheduler/{college}/pods/{podId}
 *    name: string
 *    active: boolean
 *
 *  /scheduler/{college}/weeks/{weekStartMs}/bookings/{bookingId}
 *    podId: string
 *    startMs: number
 *    endMs: number
 *    userId: string
 *    userName?: string     // NEW
 *    byRole: "student"
 */

type Booking = {
  podId: string;
  startMs: number;
  endMs: number;
  userId: string;
  userName?: string; // NEW
  byRole?: "student";
};

type Pod = {
  name: string;
  active?: boolean;
};

type Pending = {
  podId: string;
  dayLocal: Date;
  hh: number;
  mm: number;
  x: number;
  y: number;
};

// NEW: Resolve a friendly display name at booking time without UI changes
function getDisplayUserName(): string {
  const stored = localStorage.getItem("profileName");
  if (stored && stored.trim().length > 0) return stored.trim();

  const u = auth.currentUser;
  if (u?.displayName && u.displayName.trim().length > 0)
    return u.displayName.trim();
  if (u?.email) return u.email.split("@")[0];

  return "Student";
}

export default function SchedulerStudent() {
  const navigate = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const uid = auth.currentUser?.uid || "STUDENT_ME";

  // Week anchor (local Monday 00:00)
  const [weekStartLocal, setWeekStartLocal] = React.useState<Date>(
    startOfIsoWeekLocal(new Date())
  );
  const weekDays = React.useMemo(
    () => getWeekDaysFrom(weekStartLocal),
    [weekStartLocal]
  );
  const timeGrid = React.useMemo(() => buildTimeGrid(9, 20), []);

  // Pods (Firestore)
  const [pods, setPods] = React.useState<Record<string, Pod>>({});
  // Bookings (Firestore) for this week
  const [bookings, setBookings] = React.useState<Booking[]>([]);

  // Tabs
  const [tab, setTab] = React.useState<"availability" | "my">("availability");

  // Inline popover state
  const [pending, setPending] = React.useState<Pending | null>(null);

  // Derived (caps and filters for this week)
  const weekStartMs = weekStartLocal.getTime();
  const weekEndMs = weekStartMs + 7 * 86400000;

  const myWeekBookings = bookings.filter(
    (b) => b.userId === uid && b.startMs >= weekStartMs && b.startMs < weekEndMs
  );
  const myMinutes = myWeekBookings.reduce(
    (m, b) => m + (b.endMs - b.startMs) / 60000,
    0
  );
  const mySessions = myWeekBookings.length;

  function canBook(durationMin: 15 | 30) {
    if (mySessions >= 2)
      return { ok: false, reason: "You reached 2 sessions this week (max 2)." };
    if (myMinutes + durationMin > 30)
      return {
        ok: false,
        reason: "Weekly limit is 30 minutes (max 2 × 15min or 1 × 30min).",
      };
    return { ok: true };
  }

  // ---- Load Pods (live). If empty, seed defaults (Pod-1..Pod-4) once.
  React.useEffect(() => {
    if (!college) return;
    const podsCol = collection(fs, "scheduler", college, "pods");
    let seeded = false;

    const unsub = onSnapshot(podsCol, async (snap) => {
      if (snap.empty && !seeded) {
        seeded = true;
        // Seed 4 default pods
        const batch = writeBatch(fs);
        const defaults: Record<string, Pod> = {
          "Pod-1": { name: "Pod-1", active: true },
          "Pod-2": { name: "Pod-2", active: true },
          "Pod-3": { name: "Pod-3", active: true },
          "Pod-4": { name: "Pod-4", active: true },
        };
        Object.entries(defaults).forEach(([id, pod]) => {
          batch.set(doc(fs, "scheduler", college, "pods", id), pod);
        });
        await batch.commit();
        // After commit, onSnapshot will fire again with data
        return;
      }

      const map: Record<string, Pod> = {};
      snap.forEach((d) => {
        map[d.id] = (d.data() as Pod) || { name: d.id, active: true };
      });
      setPods(map);
    });

    return () => unsub();
  }, [college]);

  // ---- Load Bookings (live) for the selected week
  React.useEffect(() => {
    if (!college) return;
    const bookingsCol = collection(
      fs,
      "scheduler",
      college,
      "weeks",
      String(weekStartMs),
      "bookings"
    );

    const unsub = onSnapshot(bookingsCol, (snap) => {
      const list: Booking[] = [];
      snap.forEach((d) => {
        const v = d.data() as Booking;
        // Safety: ensure types
        if (
          v &&
          typeof v.podId === "string" &&
          typeof v.startMs === "number" &&
          typeof v.endMs === "number" &&
          typeof v.userId === "string"
        ) {
          list.push({
            podId: v.podId,
            startMs: v.startMs,
            endMs: v.endMs,
            userId: v.userId,
            userName: (v as any).userName, // carry through if present
            byRole: v.byRole,
          });
        }
      });
      setBookings(list);
    });

    return () => unsub();
  }, [college, weekStartMs]);

  function onCellClick(
    e: React.MouseEvent<HTMLButtonElement>,
    podId: string,
    dayLocal: Date,
    hh: number,
    mm: number
  ) {
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
    const x = Math.min(rect.left + rect.width + 8, window.innerWidth - 220);
    const y = Math.min(rect.top, window.innerHeight - 120);
    setPending({ podId, dayLocal, hh, mm, x, y });
  }

  async function handleBook(duration: 15 | 30) {
    if (!pending) return;
    const check = canBook(duration);
    if (!check.ok) {
      alert(check.reason);
      return;
    }
    const start = new Date(
      pending.dayLocal.getFullYear(),
      pending.dayLocal.getMonth(),
      pending.dayLocal.getDate(),
      pending.hh,
      pending.mm,
      0,
      0
    );
    const startMs = start.getTime();
    const endMs = startMs + duration * 60_000;

    // Overlap check for this pod against current in-memory bookings
    const overlap = bookings.some(
      (b) =>
        b.podId === pending.podId &&
        Math.max(b.startMs, startMs) < Math.min(b.endMs, endMs)
    );
    if (overlap) {
      alert("That slot is already taken.");
      return;
    }

    const bookingId = `${pending.podId}_${startMs}`;
    const bookingDoc = doc(
      fs,
      "scheduler",
      college,
      "weeks",
      String(weekStartMs),
      "bookings",
      bookingId
    );

    // NEW: resolve display name now and store it with the booking
    const userName = getDisplayUserName();

    await setDoc(bookingDoc, {
      podId: pending.podId,
      startMs,
      endMs,
      userId: uid,
      userName, // NEW
      byRole: "student",
    } as Booking);

    setPending(null);
    setTab("my");
  }

  async function cancelBooking(b: Booking) {
    const bookingId = `${b.podId}_${b.startMs}`;
    const path = doc(
      fs,
      "scheduler",
      college,
      "weeks",
      String(weekStartMs),
      "bookings",
      bookingId
    );
    await deleteDoc(path);
  }

  // Close popover on escape / outside click
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPending(null);
    }
    function onClick(e: MouseEvent) {
      const el = document.getElementById("slot-popover");
      if (!el) return;
      if (!el.contains(e.target as Node)) setPending(null);
    }
    if (pending) {
      document.addEventListener("keydown", onKey);
      document.addEventListener("mousedown", onClick);
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [pending]);

  // Active pods list (order stable by id)
  const podIds = React.useMemo(() => {
    return Object.keys(pods)
      .filter((k) => pods[k]?.active !== false)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [pods]);

  return (
    <div className="container" style={{ position: "relative" }}>
      <div className="row" style={{ alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Scheduler</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>

      <div className="row" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="pill">
          College: <b style={{ marginLeft: 6 }}>{college}</b>
        </div>
        <div className="pill">
          Week:&nbsp;
          <b style={{ marginLeft: 6 }}>
            {fmtDate(weekDays[0])} – {fmtDate(weekDays[6])}
          </b>
        </div>
        <div className="pill">
          Allowance:{" "}
          <b style={{ marginLeft: 6 }}>
            {myMinutes}/30 min • {mySessions}/2 sessions
          </b>
        </div>
      </div>

      <div
        className="row"
        style={{ marginTop: 12, justifyContent: "space-between", gap: 12 }}
      >
        <div className="row" style={{ gap: 8 }}>
          <button
            className={`btn ${tab === "availability" ? "primary" : ""}`}
            onClick={() => setTab("availability")}
          >
            Available Slots
          </button>
          <button
            className={`btn ${tab === "my" ? "primary" : ""}`}
            onClick={() => setTab("my")}
          >
            My Bookings
          </button>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(addDays(weekStartLocal, -7))}
          >
            ‹ Prev
          </button>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(startOfIsoWeekLocal(new Date()))}
          >
            This week
          </button>
          <button
            className="btn"
            onClick={() => setWeekStartLocal(addDays(weekStartLocal, 7))}
          >
            Next ›
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{ marginTop: 12, overflowX: "auto", position: "relative" }}
      >
        {tab === "availability" ? (
          <WeekGrid
            pods={podIds}
            podsMap={pods}
            weekDays={weekDays}
            timeGrid={timeGrid}
            bookings={bookings}
            onCellClick={onCellClick}
            uid={uid}
          />
        ) : (
          <MyBookings
            list={[...myWeekBookings].sort((a, b) => a.startMs - b.startMs)}
            podsMap={pods} // NEW: pass pods to show Display Name
            onCancel={cancelBooking}
          />
        )}
      </div>

      {/* Inline popover near the clicked slot */}
      {pending && (
        <div
          id="slot-popover"
          style={{
            position: "fixed",
            left: pending.x,
            top: pending.y,
            zIndex: 9999,
            background: "var(--surface-1)",
            border: "1px solid var(--line)",
            borderRadius: 10,
            padding: 10,
            boxShadow: "0 10px 30px rgba(0,0,0,.35)",
            minWidth: 220,
          }}
        >
          <div className="small" style={{ marginBottom: 8 }}>
            <b>{pending.podId}</b> — {fmtDate(pending.dayLocal)}{" "}
            {pad(pending.hh)}:{pad(pending.mm)}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="btn primary" onClick={() => handleBook(15)}>
              Book 15 min
            </button>
            <button className="btn" onClick={() => handleBook(30)}>
              Book 30 min
            </button>
            <button className="btn" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- UI parts ---------------- */

function WeekGrid({
  pods,
  podsMap,
  weekDays,
  timeGrid,
  bookings,
  onCellClick,
  uid,
}: {
  pods: string[];
  podsMap: Record<string, Pod>;
  weekDays: Date[];
  timeGrid: { hh: number; mm: number }[];
  bookings: Booking[];
  uid: string;
  onCellClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    podId: string,
    dayLocal: Date,
    hh: number,
    mm: number
  ) => void;
}) {
  if (pods.length === 0) {
    return <p className="small">No active pods configured.</p>;
  }
  return (
    <div>
      {/* header: pods */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `140px repeat(${pods.length}, 1fr)`,
          gap: 6,
        }}
      >
        <div />
        {pods.map((p) => (
          <div key={p} className="pill" style={{ textAlign: "center" }}>
            {podsMap[p]?.name || p}
          </div>
        ))}
      </div>

      {weekDays.map((day) => (
        <div key={String(day)} style={{ marginTop: 6 }}>
          <div
            className="pill muted"
            style={{ display: "inline-block", marginBottom: 6 }}
          >
            {day.toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </div>
          {timeGrid.map(({ hh, mm }) => (
            <div
              key={`${hh}:${mm}`}
              style={{
                display: "grid",
                gridTemplateColumns: `140px repeat(${pods.length}, 1fr)`,
                gap: 6,
              }}
            >
              <div
                className="small"
                style={{ opacity: 0.7, textAlign: "right", paddingRight: 8 }}
              >
                {pad(hh)}:{pad(mm)}
              </div>
              {pods.map((podId) => {
                const startMs = new Date(
                  day.getFullYear(),
                  day.getMonth(),
                  day.getDate(),
                  hh,
                  mm,
                  0,
                  0
                ).getTime();
                const endMs = startMs + 15 * 60_000;

                const hit = bookings.find(
                  (b) =>
                    b.podId === podId &&
                    Math.max(b.startMs, startMs) < Math.min(b.endMs, endMs)
                );
                let bg = "rgba(34,197,94,.12)"; // free (green tint)
                let border = "rgba(34,197,94,.5)";
                let label = "Free";
                const disabled = !!hit;
                if (hit) {
                  const mine = hit.userId === uid;
                  bg = mine ? "rgba(59,130,246,.12)" : "rgba(239,68,68,.12)";
                  border = mine ? "rgba(59,130,246,.5)" : "rgba(239,68,68,.5)";
                  label = mine ? "Your slot" : "Booked";
                }
                return (
                  <button
                    key={podId}
                    className="btn"
                    disabled={disabled}
                    onClick={(e) => onCellClick(e, podId, day, hh, mm)}
                    style={{
                      justifySelf: "stretch",
                      textAlign: "center",
                      background: bg,
                      borderColor: border,
                      fontSize: 12,
                      padding: "8px 6px",
                    }}
                    title={disabled ? label : "Click to book"}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MyBookings({
  list,
  podsMap,
  onCancel,
}: {
  list: Booking[];
  podsMap: Record<string, Pod>; // NEW
  onCancel: (b: Booking) => void;
}) {
  if (list.length === 0)
    return <p className="small">No bookings this week yet.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Pod</th>
          <th>When</th>
          <th>Duration</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {list.map((b) => {
          const d = new Date(b.startMs);
          const dur = (b.endMs - b.startMs) / 60000;
          const label =
            d.toLocaleDateString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
            }) +
            " " +
            d.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            });
          const podLabel = podsMap[b.podId]?.name || b.podId; // NEW: Display Name
          return (
            <tr key={`${b.podId}_${b.startMs}`}>
              <td>{podLabel}</td>
              <td>{label}</td>
              <td>{dur} min</td>
              <td>
                <button className="btn" onClick={() => onCancel(b)}>
                  Cancel
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* --------------- helpers --------------- */

function startOfIsoWeekLocal(d: Date) {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const day = copy.getDay(); // Sun=0..Sat=6
  const mondayDiff = (day + 6) % 7;
  copy.setDate(copy.getDate() - mondayDiff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function addDays(d: Date, days: number) {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + days);
  return startOfIsoWeekLocal(x); // keep anchor at Monday
}
function getWeekDaysFrom(weekStartLocal: Date) {
  return Array.from(
    { length: 7 },
    (_, i) => new Date(weekStartLocal.getTime() + i * 86400000)
  );
}
function buildTimeGrid(fromHour: number, toHour: number) {
  const out: { hh: number; mm: number }[] = [];
  for (let h = fromHour; h <= toHour; h++) {
    for (let m = 0; m < 60; m += 15) out.push({ hh: h, mm: m }); // keep 15-min slots
  }
  return out;
}
function pad(n: number) {
  return String(n).padStart(2, "0");
}
function fmtDate(d: Date) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
